/**
 * Workshop city PDP — fetch slots from app (not Liquid metaobjects).
 */
(function (global) {
  /* First session/catalog load. 3s aborted real bookings (AU API + mobile). */
  var DEFAULT_FETCH_TIMEOUT_MS = 15000;
  /* Background capacity poll — fail fast, keep last slots. Not for load_slots. */
  var DEFAULT_LIVE_STATE_TIMEOUT_MS = 3000;
  var LIVE_STATE_CHUNK_SIZE = 150;
  var DROP_SLOT_STATUSES = { draft: 1, completed: 1, ended: 1, cancelled: 1 };

  function shopDomain() {
    try {
      if (global.Shopify && global.Shopify.shop) return String(global.Shopify.shop);
    } catch (_) {}
    return '';
  }

  function normalizeStatus(raw) {
    if (raw && typeof raw === 'object') {
      raw = raw.value != null ? raw.value : raw.status;
    }
    return String(raw || 'active')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/-/g, '_');
  }

  function isDroppedSlotStatus(status) {
    return !!DROP_SLOT_STATUSES[normalizeStatus(status)];
  }

  function readCapacityRemaining(slot) {
    if (!slot) return null;
    var cap = slot.capacity_remaining;
    if (cap == null || cap === '') cap = slot._raw && slot._raw.capacity_remaining;
    if (cap == null || cap === '') return null;
    var n = Number(cap);
    return Number.isFinite(n) ? n : null;
  }

  /* Bookable = the session can take at least one seat. Remaining-seat count is a
     separate question (remainingSeats): unknown capacity is still bookable. */
  function isSlotBookable(slot) {
    if (!slot) return false;
    var ds = String(slot.display_status || '')
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/-/g, '_');
    if (ds === 'hidden' || ds === 'ended' || ds === 'sold_out') return false;
    var cap = readCapacityRemaining(slot);
    if (ds === 'bookable' || ds === 'active' || ds === 'available') {
      return !(cap != null && cap <= 0);
    }
    var st = normalizeStatus(slot.status);
    if (st === 'sold_out' || isDroppedSlotStatus(st)) return false;
    if (cap != null && cap <= 0) return false;
    return true;
  }

  /* Seat pool for one session. 0 = none left / unbookable. null = bookable but
     remaining seats are not known — callers must not invent a UI cap of 1. */
  function remainingSeats(slot) {
    if (!isSlotBookable(slot)) return 0;
    var cap = readCapacityRemaining(slot);
    return cap == null ? null : Math.max(0, cap);
  }

  function pad2(n) {
    return (n < 10 ? '0' : '') + n;
  }

  function shopTodayKey() {
    try {
      var fmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      return fmt.format(new Date());
    } catch (_) {
      var d = new Date();
      return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
    }
  }

  function toIsoDateKey(raw) {
    if (raw == null || raw === '') return '';
    var s = String(raw).trim();
    var m = s.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
    var d = new Date(s);
    if (isNaN(d.getTime())) return '';
    return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
  }

  function ymdOk(s) {
    return /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));
  }

  function readDateWindow(data, requestOpts) {
    requestOpts = requestOpts || {};
    var from =
      (data && (data.dateFrom || data.date_from)) ||
      requestOpts.date_from ||
      requestOpts.dateFrom ||
      '';
    var to =
      (data && (data.dateTo || data.date_to)) || requestOpts.date_to || requestOpts.dateTo || '';
    from = String(from || '').trim();
    to = String(to || '').trim();
    return {
      from: ymdOk(from) ? from : '',
      to: ymdOk(to) ? to : '',
    };
  }

  function clipSlotsToDateWindow(slots, from, to) {
    if (!from && !to) return slots || [];
    return (slots || []).filter(function (slot) {
      var dk = slot && slot.date_key ? String(slot.date_key).slice(0, 10) : '';
      if (!dk) return false;
      if (from && dk < from) return false;
      if (to && dk > to) return false;
      return true;
    });
  }

  function defaultDateWindow(lookaheadMonths) {
    var look = lookaheadMonths == null ? 1 : Number(lookaheadMonths);
    if (!Number.isFinite(look) || look < 0) look = 1;
    var today = shopTodayKey();
    var y = Number(today.slice(0, 4));
    var m = Number(today.slice(5, 7));
    var Cal = global.WorkshopBookingCalendarWindow;
    var bounds;
    if (Cal && typeof Cal.windowDateBounds === 'function') {
      bounds = Cal.windowDateBounds(y, m, look);
    } else {
      var endYm = m + look;
      var endY = y;
      while (endYm > 12) {
        endYm -= 12;
        endY += 1;
      }
      var last = new Date(endY, endYm, 0).getDate();
      bounds = {
        from: y + '-' + pad2(m) + '-01',
        to: endY + '-' + pad2(endYm) + '-' + pad2(last),
      };
    }
    if (bounds.from < today) bounds.from = today;
    return bounds;
  }

  function withDefaultDateWindow(data, lookaheadMonths) {
    var w = defaultDateWindow(lookaheadMonths);
    var out = Object.assign({}, data || {});
    if (!out.dateFrom && !out.date_from) out.dateFrom = w.from;
    if (!out.dateTo && !out.date_to) out.dateTo = w.to;
    return out;
  }

  /** Explicit dates win; otherwise derive from openEnded, lookaheadMonths or horizonDays. No opts = no window. */
  function resolveDateWindow(data, opts) {
    opts = opts || {};
    var win = readDateWindow(data, opts);
    if (win.from || win.to) return win;
    // Floor at today so a catalog that still carries past sessions cannot surface them.
    if (opts.openEnded) return { from: shopTodayKey(), to: '' };
    if (opts.lookaheadMonths != null) return defaultDateWindow(opts.lookaheadMonths);
    if (opts.horizonDays != null) return horizonDateWindow(opts.horizonDays);
    return win;
  }

  function workshopTypeFromHandle(handle) {
    var h = String(handle || '').toLowerCase();
    if (h.indexOf('ceramic') !== -1) return 'ceramic';
    if (h.indexOf('resin') !== -1) return 'resin';
    if (h.indexOf('flower') !== -1 || h.indexOf('floral') !== -1) return 'flower';
    if (h.indexOf('mosaic') !== -1) return 'mosaic';
    return '';
  }

  function addDaysYmd(ymd, days) {
    var p = String(ymd || '').split('-').map(Number);
    if (p.length < 3 || !p[0]) return '';
    var d = new Date(p[0], p[1] - 1, p[2] + Number(days));
    if (isNaN(d.getTime())) return '';
    return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
  }

  function horizonDateWindow(days) {
    var n = Number(days);
    if (!Number.isFinite(n) || n < 1) n = 30;
    var today = shopTodayKey();
    return { from: today, to: addDaysYmd(today, n) };
  }

  function monthDateBounds(year, month1to12) {
    var Cal = global.WorkshopBookingCalendarWindow;
    if (Cal && typeof Cal.monthDateBounds === 'function') {
      return Cal.monthDateBounds(year, month1to12);
    }
    var y = Number(year);
    var m = Number(month1to12);
    var last = new Date(y, m, 0).getDate();
    return {
      from: y + '-' + pad2(m) + '-01',
      to: y + '-' + pad2(m) + '-' + pad2(last),
    };
  }

  /** Both bounds required: an open-ended window says nothing about which months are complete. */
  function ymKeysInRange(from, to) {
    var fs = String(from || '');
    var ts = String(to || '');
    if (!ymdOk(fs) || !ymdOk(ts)) return [];
    var y = Number(fs.slice(0, 4));
    var m = Number(fs.slice(5, 7));
    var ey = Number(ts.slice(0, 4));
    var em = Number(ts.slice(5, 7));
    var out = [];
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(ey) || !Number.isFinite(em)) return out;
    var guard = 0;
    while (guard < 36) {
      out.push(y + '-' + pad2(m));
      if (y === ey && m === em) break;
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
      guard += 1;
    }
    return out;
  }

  function markLoadedMonths(root, from, to) {
    if (!root) return;
    if (!root.__wcfLoadedMonths) root.__wcfLoadedMonths = Object.create(null);
    ymKeysInRange(from, to).forEach(function (ym) {
      root.__wcfLoadedMonths[ym] = true;
    });
  }

  function slotDedupeKey(slot) {
    return (
      String((slot && (slot.gid || slot.id || slot.event_metaobject_gid)) || '') +
      '|' +
      String((slot && slot.date_key) || '').slice(0, 10) +
      '|' +
      String((slot && slot.time_label) || '')
    );
  }

  function mergeSlots(existing, incoming) {
    var out = [];
    var seen = Object.create(null);
    (existing || []).concat(incoming || []).forEach(function (slot) {
      if (!slot) return;
      var key = slotDedupeKey(slot);
      if (seen[key]) return;
      seen[key] = true;
      out.push(slot);
    });
    out.sort(function (a, b) {
      var da = String((a && a.date_key) || '');
      var db = String((b && b.date_key) || '');
      if (da !== db) return da < db ? -1 : 1;
      return String((a && a.time_label) || '').localeCompare(String((b && b.time_label) || ''));
    });
    return out;
  }

  /**
   * Generic slot query. Only keeps filters that exist.
   * opts.openEnded — today onward with no upper bound if no dates on data.
   * opts.lookaheadMonths — calendar months from today if no dates on data.
   * opts.horizonDays — today + N days if no dates on data.
   * Infer workshop_type from product handle unless opts.inferWorkshopType === false.
   */
  function normalizeSlotQuery(data, opts) {
    opts = opts || {};
    data = data || {};
    var productId = data.productId || data.product_id || '';
    var productHandle = data.productHandle || data.product_handle || '';
    var workshopType = String(data.workshopType || data.workshop_type || '')
      .trim()
      .toLowerCase();
    if (!workshopType && opts.inferWorkshopType !== false) {
      workshopType = workshopTypeFromHandle(productHandle);
    }
    var win = resolveDateWindow(data, opts);
    var out = {};
    if (productId) out.productId = productId;
    if (productHandle) out.productHandle = productHandle;
    if (data.sectionId || data.section_id) out.sectionId = data.sectionId || data.section_id;
    if (workshopType) out.workshopType = workshopType;
    if (win.from) out.dateFrom = win.from;
    if (win.to) out.dateTo = win.to;
    return out;
  }

  function assignIf(body, key, val) {
    if (val == null) return;
    var s = typeof val === 'string' ? val.trim() : val;
    if (s === '') return;
    body[key] = s;
  }

  function normalizeSlot(raw) {
    if (!raw || typeof raw !== 'object') return null;

    var status =
      raw.status != null && raw.status !== '' ? normalizeStatus(raw.status) : 'active';
    if (isDroppedSlotStatus(status)) return null;
    if (status !== 'sold_out') status = 'active';

    var displayStatus = '';
    if (raw.display_status != null && raw.display_status !== '') {
      displayStatus = String(raw.display_status)
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/-/g, '_');
    }
    if (displayStatus === 'hidden' || displayStatus === 'ended') return null;

    var gid = raw.gid || raw.metaobject_gid || raw.event_metaobject_gid || '';
    if (!gid && raw.id != null) {
      var idStr = String(raw.id);
      gid = idStr.indexOf('gid://') === 0 ? idStr : 'gid://shopify/Metaobject/' + idStr;
    }

    var dateKey = toIsoDateKey(raw.date_key);
    if (!dateKey && raw.schema_start) dateKey = toIsoDateKey(raw.schema_start);
    if (!dateKey && raw.starts_at) dateKey = toIsoDateKey(raw.starts_at);

    var timeLabel = String(raw.time_label || '').trim();
    var displayLabel = String(raw.display_label || '').trim();
    if (!displayLabel) {
      displayLabel = dateKey || '';
      if (timeLabel) displayLabel = displayLabel ? displayLabel + ' | ' + timeLabel : timeLabel;
    }
    if (!displayLabel) displayLabel = String(raw.handle || raw.event_name || 'Session');

    return {
      gid: gid,
      handle: String(raw.handle || raw.system_handle || ''),
      status: status,
      display_status: displayStatus,
      capacity_remaining:
        raw.capacity_remaining != null
          ? raw.capacity_remaining
          : raw.capacity != null
            ? raw.capacity
            : null,
      variant_id: raw.variant_id != null ? raw.variant_id : raw.variantId,
      schema_start: raw.schema_start || '',
      schema_end: raw.schema_end || '',
      date_key: dateKey,
      time_label: timeLabel,
      display_label: displayLabel,
      venue_ref_id: String(raw.venue_ref_id || (raw.venue && raw.venue.id) || '').trim(),
      venue_name: String(raw.venue_name || raw.venue || '').trim(),
      _raw: raw,
    };
  }

  function extractSlotsPayload(body) {
    if (!body) return [];
    if (Array.isArray(body)) return body;
    if (typeof body !== 'object') return [];
    if (Array.isArray(body.slots)) return body.slots;
    if (Array.isArray(body.events)) return body.events;
    if (Array.isArray(body.sessions)) return body.sessions;
    if (body.slots && typeof body.slots === 'object') {
      return Object.keys(body.slots)
        .sort()
        .map(function (k) {
          return body.slots[k];
        })
        .filter(Boolean);
    }
    if (body.data && Array.isArray(body.data.slots)) return body.data.slots;
    if (body.data && Array.isArray(body.data.events)) return body.data.events;
    if (body.result && Array.isArray(body.result.slots)) return body.result.slots;
    if (body.payload && Array.isArray(body.payload.slots)) return body.payload.slots;
    return [];
  }

  function resolveEventsApiUrl(root) {
    var CFG = global.WorkshopBookingConfig || global.WorkshopCityConfig;
    if (CFG && typeof CFG.resolveEventsApiUrl === 'function') {
      var fromCfg = CFG.resolveEventsApiUrl(root);
      if (fromCfg) return fromCfg;
    }
    var events = root && root.getAttribute && root.getAttribute('data-events-api-url');
    if (events) return String(events).trim();
    if (global.document) {
      var el = global.document.querySelector('[data-events-api-url]');
      if (el) {
        events = el.getAttribute('data-events-api-url');
        if (events) return String(events).trim();
      }
    }
    return '';
  }

  function buildRequest(root, data, requestOpts) {
    requestOpts = requestOpts || {};
    var url = resolveEventsApiUrl(root);
    var q = normalizeSlotQuery(data, requestOpts);
    var body = {
      shop_domain: shopDomain(),
      source: requestOpts.source || 'workshop_city_flow',
      intent: requestOpts.intent || 'load_slots',
    };
    assignIf(body, 'product_id', q.productId);
    assignIf(body, 'product_handle', q.productHandle);
    assignIf(body, 'section_id', q.sectionId);
    assignIf(body, 'venue_ref_id', requestOpts.venue_ref_id);
    assignIf(body, 'venue_id', requestOpts.venue_id);
    assignIf(body, 'workshop_type', q.workshopType);
    assignIf(body, 'date_from', q.dateFrom);
    assignIf(body, 'date_to', q.dateTo);
    return { url: url, body: body };
  }

  function isDebug() {
    try {
      return new URLSearchParams(global.location.search).get('wcf_debug') === '1';
    } catch (_) {
      return false;
    }
  }

  function fetchWithTimeout (url, options, timeoutMs) {
    timeoutMs = timeoutMs || DEFAULT_FETCH_TIMEOUT_MS;
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = null;
    var fetchOpts = Object.assign({}, options || {});

    if (controller) {
      fetchOpts.signal = controller.signal;
      timer = setTimeout(function () {
        controller.abort();
      }, timeoutMs);
    }

    var fetchPromise = fetch(url, fetchOpts);
    return fetchPromise
      .then(function (res) {
        if (timer) clearTimeout(timer);
        return res;
      })
      .catch(function (err) {
        if (timer) clearTimeout(timer);
        if (err && err.name === 'AbortError') {
          throw new Error('Request timed out after ' + timeoutMs + 'ms');
        }
        throw err;
      });
  }

  function postJsonWithTimeout (url, body, timeoutMs) {
    return fetchWithTimeout (url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }, timeoutMs).then(function (res) {
      return res.json().then(function (json) {
        return { ok: res.ok, status: res.status, body: json };
      });
    });
  }

  function deriveNearbyCatalogUrl(root) {
    var explicit =
      (root && root.getAttribute('data-nearby-catalog-api-url')) ||
      (root && root.getAttribute('data-nearby-api-url'));
    if (explicit) return explicit;

    var eventsUrl = resolveEventsApiUrl(root);
    if (!eventsUrl) return '';
    if (eventsUrl.indexOf('/workshop-city/events') !== -1) {
      return eventsUrl.replace('/workshop-city/events', '/workshop-city/nearby-catalog');
    }
    return eventsUrl.replace(/\/events?\/?$/, '/nearby-catalog');
  }

  function fetchSlots(root, data, timeoutMs, requestOpts) {
    requestOpts = requestOpts || {};
    data = normalizeSlotQuery(data, requestOpts);
    if (root) root.__wcfLastSlotQuery = data;
    var req = buildRequest(root, data, requestOpts);
    if (!req.url) {
      return Promise.reject(new Error('Workshop events API URL is not configured.'));
    }
    if (isDebug()) console.info('[workshop-city-events] POST', req.url, req.body);
    return postJsonWithTimeout (req.url, req.body, timeoutMs)
      .then(function (r) {
        if (isDebug()) console.info('[workshop-city-events] response', r.status, r.body);
        if (!r.ok) {
          var msg =
            (r.body && (r.body.description || r.body.message || r.body.error)) ||
            'Events API returned ' + r.status;
          if (r.status === 401) msg = 'unauthorized (401) — public events API requires app fix';
          throw new Error(msg);
        }
        var body = r.body;
        if (typeof body === 'string') {
          try {
            body = JSON.parse(body);
          } catch (_) {
            body = {};
          }
        }
        var list = extractSlotsPayload(body);
        var normalized = list.map(normalizeSlot).filter(Boolean);
        if (list.length && !normalized.length) {
          console.warn('[workshop-city-events] normalize dropped all slots; using raw fallback', list);
          normalized = list
            .filter(function (raw) {
              return raw && typeof raw === 'object';
            })
            .map(function (raw) {
              return normalizeSlot(
                Object.assign({}, raw, {
                  status: raw.status || 'active',
                })
              );
            })
            .filter(Boolean);
        }
        if (isDebug() && list.length && !normalized.length) {
          console.warn('[workshop-city-events] all slots dropped after fallback', list);
        }
        return applyConfigMetaFromResponse(root, body).then(function () {
          var dateWin = readDateWindow(data, requestOpts);
          var clip = requestOpts.clipDateWindow !== false;
          if (clip && (dateWin.from || dateWin.to)) {
            normalized = clipSlotsToDateWindow(normalized, dateWin.from, dateWin.to);
          }
          if (dateWin.from || dateWin.to) markLoadedMonths(root, dateWin.from, dateWin.to);
          return normalized;
        });
      });
  }

  function fetchCatalogSlots(root, data, opts) {
    opts = opts || {};
    var timeoutMs = opts.timeoutMs != null ? opts.timeoutMs : DEFAULT_FETCH_TIMEOUT_MS;
    var requestOpts = {
      source: opts.source || 'workshop_city_flow',
      intent: opts.intent || 'load_slots',
      venue_ref_id: opts.venue_ref_id || opts.venueRefId || '',
      venue_id: opts.venue_id || opts.venueId || '',
      date_from: opts.date_from || opts.dateFrom || '',
      date_to: opts.date_to || opts.dateTo || '',
      clipDateWindow: opts.clipDateWindow,
    };
    return fetchSlots(root, data, timeoutMs, requestOpts);
  }

  function resolveValidateApiUrl(root) {
    var CFG = global.WorkshopBookingConfig || global.WorkshopCityConfig;
    if (CFG && typeof CFG.resolveValidateApiUrl === 'function') {
      return CFG.resolveValidateApiUrl(root);
    }
    var events = resolveEventsApiUrl(root);
    if (events) {
      return String(events).replace(/\/workshop-city\/events\/?$/, '/workshop-city/capacity/validate');
    }
    return '';
  }

  function validationUnavailableResult(root) {
    var CFG = global.WorkshopBookingConfig || global.WorkshopCityConfig;
    if (CFG && typeof CFG.validateFailModeOpen === 'function' && CFG.validateFailModeOpen(root)) {
      return null;
    }
    return {
      ok: false,
      allowed: false,
      message: 'We could not verify workshop capacity right now. Please try again.',
    };
  }

  function liveFetchFailModeOpen(root) {
    var CFG = global.WorkshopBookingConfig || global.WorkshopCityConfig;
    if (CFG && typeof CFG.liveFetchFailModeOpen === 'function') {
      return CFG.liveFetchFailModeOpen(root);
    }
    var policy =
      (CFG && typeof CFG.readBootstrap === 'function' && CFG.readBootstrap(root).policy) || {};
    return String(policy.live_fetch_fail_mode || 'closed').toLowerCase() === 'open';
  }

  function fetchCapacityValidate(root, params, timeoutMs) {
    params = params || {};
    var intent = params.intent || 'add_to_cart';
    var CFG = global.WorkshopBookingConfig || global.WorkshopCityConfig;
    if (!CFG || CFG.TEMP_DISABLE_CAPACITY_VALIDATE === true) {
      return Promise.resolve(null);
    }
    if (typeof CFG.validateGateEnabled === 'function' && !CFG.validateGateEnabled(root, intent)) {
      return Promise.resolve(null);
    }

    var url = resolveValidateApiUrl(root);
    if (!url) {
      return Promise.resolve(validationUnavailableResult(root));
    }
    var body = {
      shop_domain: shopDomain(),
      event_id: params.event_id || params.eventId || params.gid || '',
      requested_total_qty:
        params.requested_total_qty != null
          ? params.requested_total_qty
          : params.requestedTotalQty != null
            ? params.requestedTotalQty
            : null,
      intent: intent,
    };
    if (params.line_key != null) body.line_key = params.line_key;
    if (params.current_qty != null) body.current_qty = params.current_qty;

    return postJsonWithTimeout(url, body, timeoutMs || DEFAULT_FETCH_TIMEOUT_MS)
      .then(function (r) {
        if (r.status === 501 || r.status === 405) return validationUnavailableResult(root);
        var b = r.body || {};
        if (!r.ok) {
          return {
            ok: false,
            allowed: false,
            message:
              b.message ||
              b.description ||
              b.error ||
              'We could not verify workshop capacity right now. Please try again.',
            description: b.description,
            http_status: r.status,
          };
        }
        return {
          ok: true,
          allowed: b.allowed !== false && b.valid !== false,
          capacity_remaining: b.capacity_remaining,
          capacity_as_of: b.capacity_as_of,
          as_of: b.as_of,
          display_status: b.display_status,
          status: b.status,
          canonical_event_id: b.canonical_event_id,
          capacity_group_id: b.capacity_group_id,
          message: b.message || b.description || '',
          http_status: r.status,
        };
      })
      .catch(function () {
        return validationUnavailableResult(root);
      });
  }

  function extractStatePayload(body) {
    if (!body || typeof body !== 'object') return null;
    if (body.cities || body.state) return body;
    if (body.data && (body.data.cities || body.data.state)) return body.data;
    return null;
  }

  function extractConfigVersion(body) {
    if (!body || typeof body !== 'object') return null;
    if (body.config_version != null) return body.config_version;
    if (body.display_config && body.display_config.version != null) return body.display_config.version;
    if (body.data && body.data.config_version != null) return body.data.config_version;
    return null;
  }

  function normalizeCity(raw, variantsByHandle, metaByHandle) {
    if (!raw || typeof raw !== 'object') return null;
    var handle = String(raw.product_handle || raw.handle || '').trim();
    var slotsRaw = raw.slots || raw.events || [];
    var slots = (Array.isArray(slotsRaw) ? slotsRaw : []).map(normalizeSlot).filter(Boolean);
    var variants = raw.variants;
    if (!Array.isArray(variants) || !variants.length) {
      variants =
        handle && variantsByHandle && variantsByHandle[handle]
          ? variantsByHandle[handle]
          : [];
    }
    var city = {
      product_id: raw.product_id,
      product_handle: handle,
      product_url: raw.product_url || (handle ? '/products/' + handle : ''),
      city_name: String(raw.city_name || raw.city || '').trim(),
      venue_name: String(raw.venue_name || '').trim(),
      featured_image_url: raw.featured_image_url || '',
      rating_value: raw.rating_value,
      review_count: raw.review_count,
      slots: slots,
      variants: variants,
      next_session_label: raw.next_session_label || '',
    };
    if (global.WorkshopCityFlow && global.WorkshopCityFlow.mergeCityMeta) {
      city = global.WorkshopCityFlow.mergeCityMeta(city, metaByHandle);
    } else if (metaByHandle && handle && metaByHandle[handle]) {
      var m = metaByHandle[handle];
      if (!city.featured_image_url && m.featured_image_url) city.featured_image_url = m.featured_image_url;
      if (!city.product_url && m.product_url) city.product_url = m.product_url;
      if (m.latitude != null) city.latitude = m.latitude;
      if (m.longitude != null) city.longitude = m.longitude;
    }
    return city;
  }

  function buildStateRequest(root, data, requestOpts) {
    requestOpts = requestOpts || {};
    var url = root && root.getAttribute('data-state-api-url');
    if (!url) {
      var eventsUrl = resolveEventsApiUrl(root);
      url = eventsUrl
        ? String(eventsUrl).replace(/\/workshop-city\/events\/?$/, '/workshop-city/state-events')
        : '';
    }
    var body = {
      shop_domain: shopDomain(),
      state_code: data && data.stateCode,
      state_name: data && data.stateName,
      collection_handle: data && data.collectionHandle,
      collection_id: data && data.collectionId,
      source: 'workshop_state_collection',
      intent: 'load_state_catalog',
    };
    // A state catalog spans every workshop type, so the type is never inferred here —
    // it goes out only when a caller asks for one.
    var win = resolveDateWindow(data, requestOpts);
    assignIf(body, 'date_from', win.from);
    assignIf(body, 'date_to', win.to);
    assignIf(body, 'workshop_type', data && (data.workshopType || data.workshop_type));
    return { url: url, body: body };
  }

  function fetchNearbyCatalog(root, timeoutMs) {
    var url = deriveNearbyCatalogUrl(root);
    var body = {
      shop_domain: shopDomain(),
      intent: 'load_nearby_catalog',
    };
    if (isDebug()) console.info('[workshop-city-events] POST nearby-catalog', url, body);
    return postJsonWithTimeout (url, body, timeoutMs)
      .then(function (r) {
        if (isDebug()) console.info('[workshop-city-events] nearby-catalog response', r.status, r.body);
        if (!r.ok) {
          var msg =
            (r.body && (r.body.description || r.body.message || r.body.error)) ||
            'Nearby catalog API returned ' + r.status;
          throw new Error(msg);
        }
        var payload = r.body;
        if (typeof payload === 'string') {
          try {
            payload = JSON.parse(payload);
          } catch (_) {
            payload = {};
          }
        }
        var products = payload && payload.products;
        return Array.isArray(products) ? products : [];
      });
  }

  /* Hub summary is opt-in: no URL attribute means the endpoint is not live yet and
     every hub caller falls back to the per-city /events path. */
  function resolveHubSummaryUrl(root) {
    var explicit = root && root.getAttribute && root.getAttribute('data-hub-summary-api-url');
    if (explicit) return String(explicit).trim();
    if (global.document) {
      var el = global.document.querySelector('[data-hub-summary-api-url]');
      if (el) {
        var attr = el.getAttribute('data-hub-summary-api-url');
        if (attr) return String(attr).trim();
      }
    }
    return '';
  }

  function normalizeHubSummaryDay(raw) {
    if (!raw) return null;
    var key = toIsoDateKey(typeof raw === 'object' ? raw.d || raw.date_key || raw.date : raw);
    if (!key) return null;
    var total = Number(typeof raw === 'object' ? (raw.n != null ? raw.n : raw.count) : 0);
    var bookable = Number(typeof raw === 'object' ? (raw.b != null ? raw.b : raw.bookable) : 0);
    return {
      date_key: key,
      count: Number.isFinite(total) ? total : 0,
      bookable: Number.isFinite(bookable) ? bookable : 0,
    };
  }

  function normalizeHubSummaryCity(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var handle = String(raw.product_handle || raw.handle || '').trim();
    var productId = String(raw.product_id || '').trim();
    if (!handle && !productId) return null;
    var days = (Array.isArray(raw.days) ? raw.days : [])
      .map(normalizeHubSummaryDay)
      .filter(Boolean);
    days.sort(function (a, b) {
      return a.date_key < b.date_key ? -1 : a.date_key > b.date_key ? 1 : 0;
    });
    var bookableCount = Number(raw.bookable_count);
    if (!Number.isFinite(bookableCount)) {
      bookableCount = days.reduce(function (sum, day) {
        return sum + day.bookable;
      }, 0);
    }
    var kind = String(raw.kind || raw.venue_kind || '').toLowerCase();
    if (kind !== 'store') kind = 'popup';
    return {
      product_id: productId,
      product_handle: handle,
      kind: kind,
      variant_id: String(raw.variant_id || '').trim(),
      has_events: raw.has_events != null ? raw.has_events === true : days.length > 0,
      bookable_count: bookableCount,
      next_date_key: toIsoDateKey(raw.next_date_key) || (days.length ? days[0].date_key : ''),
      date_to: toIsoDateKey(raw.date_to) || (days.length ? days[days.length - 1].date_key : ''),
      days: days,
    };
  }

  function fetchHubSummary(root, data, timeoutMs) {
    var url = resolveHubSummaryUrl(root);
    if (!url) return Promise.reject(new Error('Hub summary API URL is not configured.'));
    data = data || {};
    var body = {
      shop_domain: shopDomain(),
      source: 'workshop_booking_hub',
      intent: 'load_hub_summary',
    };
    assignIf(body, 'workshop_type', data.workshopType || data.workshop_type);
    assignIf(body, 'date_from', toIsoDateKey(data.dateFrom) || shopTodayKey());
    if (data.horizonDays && typeof data.horizonDays === 'object') {
      body.horizon_days = data.horizonDays;
    }
    var products = (Array.isArray(data.products) ? data.products : [])
      .map(function (p) {
        var id = String((p && (p.id || p.productId)) || '').trim();
        if (!id) return null;
        return {
          id: id,
          kind: String((p && p.kind) || '').toLowerCase() === 'store' ? 'store' : 'popup',
          handle: String((p && p.handle) || '').trim(),
        };
      })
      .filter(Boolean);
    if (products.length) body.products = products;
    var ids = (Array.isArray(data.productIds) ? data.productIds : [])
      .map(function (id) {
        return String(id || '').trim();
      })
      .filter(Boolean);
    if (ids.length) body.product_ids = ids;
    if (isDebug()) console.info('[workshop-city-events] POST hub-summary', url, body);
    return postJsonWithTimeout(url, body, timeoutMs).then(function (r) {
      if (isDebug()) console.info('[workshop-city-events] hub-summary response', r.status, r.body);
      if (!r.ok) {
        var msg =
          (r.body && (r.body.description || r.body.message || r.body.error)) ||
          'Hub summary API returned ' + r.status;
        throw new Error(msg);
      }
      var payload = r.body;
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch (_) {
          payload = {};
        }
      }
      if (payload && payload.data && payload.data.cities) payload = payload.data;
      var citiesRaw = (payload && payload.cities) || [];
      var capacityMode = String((payload && payload.capacity_mode) || '').toLowerCase();
      return {
        as_of: (payload && payload.as_of) || '',
        // Skeleton catalogs carry no capacity, so bookable counts are unknown
        // rather than zero — callers must not paint those cities as sold out.
        capacity_known: capacityMode !== 'skeleton',
        cities: (Array.isArray(citiesRaw) ? citiesRaw : [])
          .map(function (raw) {
            var city = normalizeHubSummaryCity(raw);
            if (city) city.capacity_known = capacityMode !== 'skeleton';
            return city;
          })
          .filter(Boolean),
      };
    });
  }

  function fetchStateCatalog(root, data, timeoutMs, requestOpts) {
    var req = buildStateRequest(root, data, requestOpts);
    var variantsByHandle = (data && data.productVariants) || {};
    var metaByHandle = (data && data.productMeta) || {};
    return postJsonWithTimeout (req.url, req.body, timeoutMs)
      .then(function (r) {
        if (!r.ok) {
          var msg =
            (r.body && (r.body.description || r.body.message || r.body.error)) ||
            'State events API returned ' + r.status;
          throw new Error(msg);
        }
        var payload = extractStatePayload(r.body) || {};
        return applyConfigMetaFromResponse(root, r.body).then(function () {
          var citiesRaw = payload.cities || [];
          var cities = (Array.isArray(citiesRaw) ? citiesRaw : [])
            .map(function (c) {
              return normalizeCity(c, variantsByHandle, metaByHandle);
            })
            .filter(Boolean);
          return {
            state: payload.state || {
              code: data.stateCode,
              name: data.stateName,
            },
            stats: payload.stats || {},
            cities: cities,
            config_version: extractConfigVersion(r.body),
          };
        });
      });
  }

  function deriveLiveStreamUrl(catalogUrl) {
    if (!catalogUrl) return null;
    return String(catalogUrl)
      .replace(/\/event-live-state\/?$/, '/event-live-stream')
      .replace(/\/state-events\/?$/, '/event-live-stream')
      .replace(/\/events\/?$/, '/event-live-stream');
  }

  function resolveLiveStreamUrl(root, catalogUrl) {
    var live = resolveLiveStateApiUrl(root, catalogUrl);
    if (live) return deriveLiveStreamUrl(live);
    return deriveLiveStreamUrl(catalogUrl);
  }

  function deriveLiveStateApiUrl(catalogUrl) {
    var CFG = global.WorkshopBookingConfig || global.WorkshopCityConfig;
    if (CFG && typeof CFG.deriveLiveStateApiUrl === 'function') {
      return CFG.deriveLiveStateApiUrl(catalogUrl);
    }
    if (!catalogUrl) return null;
    return String(catalogUrl)
      .replace(/\/state-events\/?$/, '/event-live-state')
      .replace(/\/events\/?$/, '/event-live-state');
  }

  function resolveLiveStateApiUrl(root, catalogUrl) {
    var CFG = global.WorkshopBookingConfig || global.WorkshopCityConfig;
    if (CFG && typeof CFG.resolveLiveStateApiUrl === 'function') {
      return CFG.resolveLiveStateApiUrl(root, catalogUrl);
    }
    var explicit = root && root.getAttribute('data-live-state-api-url');
    if (explicit) return explicit;
    return deriveLiveStateApiUrl(catalogUrl || resolveEventsApiUrl(root));
  }

  function chunkIds(ids, size) {
    var chunks = [];
    for (var i = 0; i < ids.length; i += size) {
      chunks.push(ids.slice(i, i + size));
    }
    return chunks;
  }

  function collectEventGids(slots) {
    return (slots || [])
      .map(function (s) {
        return s && s.gid;
      })
      .filter(Boolean);
  }

  function lookupLiveState(states, gid) {
    if (!states || gid == null || gid === '') return null;
    var key = String(gid);
    if (states[key]) return states[key];
    var aliases = eventIdAliases(key);
    for (var i = 0; i < aliases.length; i++) {
      if (states[aliases[i]]) return states[aliases[i]];
    }
    return null;
  }

  function mergeSlotWithLive(slot, states, asOf) {
    if (!slot || !slot.gid) return slot;
    var live = lookupLiveState(states, slot.gid);
    if (!live) return slot;
    var liveStatus =
      live.status != null && live.status !== '' ? normalizeStatus(live.status) : '';
    var liveDisplay =
      live.display_status != null && live.display_status !== ''
        ? String(live.display_status).trim().toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_')
        : '';
    var liveCapacity = live.capacity_remaining;
    if (!liveDisplay) {
      var staleDisplay = String(slot.display_status || '').trim().toLowerCase();
      var staleSoldOut = staleDisplay === 'sold_out' || staleDisplay === 'sold-out';
      var liveLooksOpen =
        liveStatus &&
        liveStatus !== 'sold_out' &&
        !isDroppedSlotStatus(liveStatus) &&
        (liveCapacity == null || liveCapacity === '' || Number(liveCapacity) > 0);
      if (staleSoldOut && liveLooksOpen) liveDisplay = 'bookable';
    }
    var merged = Object.assign({}, slot, {
      status: liveStatus || slot.status,
      capacity_remaining:
        liveCapacity != null ? liveCapacity : slot.capacity_remaining,
      display_status: liveDisplay || slot.display_status,
      canonical_event_id:
        live.canonical_event_id != null ? live.canonical_event_id : slot.canonical_event_id,
      capacity_group_id:
        live.capacity_group_id != null ? live.capacity_group_id : slot.capacity_group_id,
    });
    if (asOf) merged.capacity_as_of = asOf;
    if (merged.display_status != null) {
      var ds = String(merged.display_status).toLowerCase();
      if (ds === 'hidden' || ds === 'ended') return null;
    }
    if (isDroppedSlotStatus(merged.status)) return null;
    if (merged.status !== 'sold_out') merged.status = 'active';
    return merged;
  }

  function mergeSlotsWithLive(slots, states, asOf) {
    return (slots || [])
      .map(function (s) {
        return mergeSlotWithLive(s, states, asOf);
      })
      .filter(Boolean);
  }

  function fetchLiveState(eventIds, opts) {
    opts = opts || {};
    var root = opts.root;
    var url = opts.url || resolveLiveStateApiUrl(root);
    if (!url || !eventIds || !eventIds.length) {
      return Promise.resolve({ as_of: null, states: {}, ok: false });
    }

    var seen = {};
    var unique = [];
    (eventIds || []).forEach(function (id) {
      if (!id) return;
      var s = String(id);
      if (seen[s]) return;
      seen[s] = true;
      unique.push(s);
    });
    if (!unique.length) return Promise.resolve({ as_of: null, states: {} });

    var timeoutMs = opts.timeoutMs != null ? opts.timeoutMs : DEFAULT_LIVE_STATE_TIMEOUT_MS;
    var chunks = chunkIds(unique, LIVE_STATE_CHUNK_SIZE);

    return Promise.all(
      chunks.map(function (chunk) {
        return postJsonWithTimeout(
          url,
          {
            shop_domain: shopDomain(),
            event_ids: chunk,
          },
          timeoutMs
        ).then(function (r) {
          if (!r.ok) {
            var msg =
              (r.body && (r.body.description || r.body.message || r.body.error)) ||
              'Live state API returned ' + r.status;
            throw new Error(msg);
          }
          var body = r.body;
          if (typeof body === 'string') {
            try {
              body = JSON.parse(body);
            } catch (_) {
              body = {};
            }
          }
          return {
            as_of: body && body.as_of,
            states: (body && body.states) || {},
            config_version: body && body.config_version != null ? body.config_version : null,
            client_config: body && body.client_config,
            ok: body && body.ok,
          };
        });
      })
    ).then(function (results) {
      var merged = { as_of: null, states: {}, config_version: null, client_config: null, ok: true };
      results.forEach(function (part) {
        if (part.as_of) merged.as_of = part.as_of;
        if (part.config_version != null) merged.config_version = part.config_version;
        if (part.client_config) merged.client_config = part.client_config;
        if (part.ok === false) merged.ok = false;
        Object.assign(merged.states, part.states || {});
      });
      var root = opts.root;
      return applyConfigMetaFromResponse(root, merged).then(function () {
        return merged;
      });
    });
  }

  function resolveBookingConfig() {
    return global.WorkshopBookingConfig || global.WorkshopCityConfig;
  }

  function applyConfigMetaFromResponse(root, body) {
    var CFG = resolveBookingConfig();
    if (root && CFG && typeof CFG.applyMetaFromResponse === 'function') {
      return CFG.applyMetaFromResponse(root, body);
    }
    return Promise.resolve();
  }

  function resolvePollIntervalMs(opts) {
    if (opts.intervalMs != null) return opts.intervalMs;
    var CFG = resolveBookingConfig();
    if (!opts.root || !CFG) return 30000;
    var ctx = typeof opts.getPollContext === 'function' ? opts.getPollContext() : null;
    if (ctx == null && typeof CFG.pollContextForRoot === 'function') {
      ctx = CFG.pollContextForRoot(opts.root);
    }
    return CFG.pollIntervalMs(opts.root, ctx);
  }

  function startLiveStatePoll(opts) {
    opts = opts || {};
    if (opts.root && resolveBookingConfig() && resolveBookingConfig().pollEnabled(opts.root) === false) {
      return { start: function () {}, stop: function () {}, refresh: function () {}, syncInterval: function () {} };
    }
    var timer = null;
    var tickHandler = null;
    var activeIntervalMs = null;
    var streamUnsub = null;
    var streamPaused = false;
    var pendingWhileHidden = false;
    var catalogTimer = 0;

    function tick() {
      if (document.visibilityState === 'hidden') {
        pendingWhileHidden = true;
        return;
      }
      pendingWhileHidden = false;
      var gids = typeof opts.getSlotGids === 'function' ? opts.getSlotGids() : [];
      if (!gids || !gids.length) return;
      var url =
        opts.url ||
        deriveLiveStateApiUrl(opts.catalogUrl) ||
        (opts.root && resolveLiveStateApiUrl(opts.root, opts.catalogUrl));
      fetchLiveState(gids, { url: url, timeoutMs: opts.timeoutMs, root: opts.root })
        .then(function (r) {
          if (typeof opts.onUpdate === 'function') opts.onUpdate(r.states || {}, r);
        })
        .catch(function () {
          /* stale — no-op */
        });
    }

    function syncInterval() {
      var next = resolvePollIntervalMs(opts);
      if (next === activeIntervalMs) return false;
      activeIntervalMs = next;
      if (opts.root) opts.root.__wcfLastPollIntervalMs = next;
      if (timer) {
        clearInterval(timer);
        timer = streamPaused ? null : setInterval(onTimer, activeIntervalMs);
      }
      return true;
    }

    function onTimer() {
      if (streamPaused) return;
      syncInterval();
      tick();
    }

    function startPollTimer() {
      if (streamPaused) return;
      if (timer) clearInterval(timer);
      activeIntervalMs = resolvePollIntervalMs(opts);
      if (opts.root) opts.root.__wcfLastPollIntervalMs = activeIntervalMs;
      timer = setInterval(onTimer, activeIntervalMs);
    }

    function onStreamEvent(evt) {
      var type = evt && evt.type;
      var scope = (evt && evt.scope) || (evt && evt.payload && evt.payload.scope);
      if (type === 'hello') {
        streamPaused = true;
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
        return;
      }
      if (type === 'catalog' || scope === 'catalog') {
        if (typeof opts.onCatalog === 'function') {
          if (catalogTimer) return;
          catalogTimer = setTimeout(function () {
            catalogTimer = 0;
            opts.onCatalog(evt && evt.payload);
          }, 400);
          return;
        }
        tick();
        return;
      }
      if (type === 'live') {
        tick();
        return;
      }
      streamPaused = false;
      startPollTimer();
    }

    function bindStream() {
      if (streamUnsub) return;
      var Stream = global.WorkshopLiveStream;
      if (!Stream || typeof Stream.subscribe !== 'function') return;
      if (typeof Stream.isSupported === 'function' && !Stream.isSupported()) return;
      streamUnsub = Stream.subscribe(onStreamEvent);
    }

    function unbindStream() {
      if (!streamUnsub) return;
      try {
        streamUnsub();
      } catch (_) {}
      streamUnsub = null;
      streamPaused = false;
    }

    function start() {
      stop();
      streamPaused = false;
      tick();
      bindStream();
      startPollTimer();
      tickHandler = function () {
        if (document.visibilityState === 'hidden') return;
        if (pendingWhileHidden) tick();
        else if (!streamPaused) tick();
      };
      document.addEventListener('visibilitychange', tickHandler);
    }

    function stop() {
      if (timer) clearInterval(timer);
      timer = null;
      activeIntervalMs = null;
      unbindStream();
      if (catalogTimer) {
        clearTimeout(catalogTimer);
        catalogTimer = 0;
      }
      if (tickHandler) {
        document.removeEventListener('visibilitychange', tickHandler);
        tickHandler = null;
      }
    }

    return { start: start, stop: stop, refresh: tick, syncInterval: syncInterval };
  }

  function numericMetaobjectId(value) {
    var s = String(value == null ? '' : value).trim();
    if (!s) return '';
    var metaGid = /^gid:\/\/shopify\/Metaobject\/(\d+)$/i.exec(s);
    if (metaGid) return metaGid[1];
    if (/^\d+$/.test(s)) return s;
    return '';
  }

  function eventIdAliases(value) {
    var s = String(value == null ? '' : value).trim();
    if (!s) return [];
    var out = [s];
    var num = numericMetaobjectId(s);
    if (num) {
      if (out.indexOf(num) === -1) out.push(num);
      var gid = 'gid://shopify/Metaobject/' + num;
      if (out.indexOf(gid) === -1) out.push(gid);
    }
    return out;
  }

  function cartLineProp(item, key) {
    if (!item || !item.properties) return '';
    var props = item.properties;
    if (typeof props === 'object' && !Array.isArray(props)) {
      return String(props[key] || '').trim();
    }
    for (var i = 0; i < props.length; i++) {
      var row = props[i];
      if (!row) continue;
      var k = row[0] != null ? row[0] : row.name;
      var v = row[1] != null ? row[1] : row.value;
      if (String(k) === key) return String(v || '').trim();
    }
    return '';
  }

  function isWorkshopAddonItem(item) {
    return cartLineProp(item, '_workshop_addon') === 'true';
  }

  function collectIdAliases(values) {
    var seen = Object.create(null);
    var out = [];
    (values || []).forEach(function (value) {
      eventIdAliases(value).forEach(function (alias) {
        if (!alias || seen[alias]) return;
        seen[alias] = true;
        out.push(alias);
      });
    });
    return out;
  }

  function idsOverlap(aVals, bVals) {
    var a = collectIdAliases(aVals);
    if (!a.length) return false;
    var seen = Object.create(null);
    a.forEach(function (id) {
      seen[id] = true;
    });
    var b = collectIdAliases(bVals);
    for (var i = 0; i < b.length; i++) {
      if (seen[b[i]]) return true;
    }
    return false;
  }

  function eventValuesFromSlot(slot) {
    if (!slot) return [];
    var raw = slot._raw && typeof slot._raw === 'object' ? slot._raw : {};
    return [
      slot.gid,
      slot.canonical_event_id,
      slot.capacity_group_id,
      slot.event_metaobject_gid,
      slot.id,
      raw.gid,
      raw.id,
      raw.event_metaobject_gid,
      raw.canonical_event_id,
      raw.capacity_group_id,
    ].filter(Boolean);
  }

  function eventValuesFromCartLine(item) {
    return [
      cartLineProp(item, '_event_id'),
      cartLineProp(item, 'event_id'),
      cartLineProp(item, '_event_metaobject_gid'),
      cartLineProp(item, '_canonical_event_id'),
      cartLineProp(item, '_capacity_group_id'),
    ].filter(Boolean);
  }

  function eventGidFromCartLine(item) {
    return (
      cartLineProp(item, '_event_metaobject_gid') ||
      cartLineProp(item, '_event_id') ||
      cartLineProp(item, 'event_id') ||
      ''
    );
  }

  function seatsInCartForSlot(slot, cart) {
    if (!cart || !Array.isArray(cart.items) || !slot) return 0;
    var target = eventValuesFromSlot(slot);
    if (!target.length) return 0;
    var total = 0;
    cart.items.forEach(function (item) {
      if (!item || isWorkshopAddonItem(item)) return;
      if (!idsOverlap(target, eventValuesFromCartLine(item))) return;
      total += item.quantity || 0;
    });
    return total;
  }

  function seatsInCartForEvent(eventGid, cart) {
    if (!cart || !eventGid) return 0;
    return seatsInCartForSlot({ gid: eventGid, canonical_event_id: eventGid }, cart);
  }

  function fetchCart() {
    var rootPath = (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/';
    return fetch(rootPath + 'cart.js', { credentials: 'same-origin' })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  function fetchCartSeatsForSlot(slot) {
    if (!slot) return Promise.resolve(0);
    return fetchCart().then(function (cart) {
      return seatsInCartForSlot(slot, cart);
    });
  }

  function fetchCartSeatsForEvent(eventGid) {
    var gid = String(eventGid || '').trim();
    if (!gid) return Promise.resolve(0);
    return fetchCartSeatsForSlot({ gid: gid, canonical_event_id: gid });
  }

  global.WorkshopCityEvents = {
    fetchWithTimeout : fetchWithTimeout ,
    postJsonWithTimeout : postJsonWithTimeout ,
    DEFAULT_FETCH_TIMEOUT_MS: DEFAULT_FETCH_TIMEOUT_MS,
    DEFAULT_LIVE_STATE_TIMEOUT_MS: DEFAULT_LIVE_STATE_TIMEOUT_MS,
    LIVE_STATE_CHUNK_SIZE: LIVE_STATE_CHUNK_SIZE,
    fetchSlots: fetchSlots,
    fetchCatalogSlots: fetchCatalogSlots,
    normalizeSlotQuery: normalizeSlotQuery,
    workshopTypeFromHandle: workshopTypeFromHandle,
    defaultDateWindow: defaultDateWindow,
    horizonDateWindow: horizonDateWindow,
    monthDateBounds: monthDateBounds,
    withDefaultDateWindow: withDefaultDateWindow,
    clipSlotsToDateWindow: clipSlotsToDateWindow,
    mergeSlots: mergeSlots,
    markLoadedMonths: markLoadedMonths,
    fetchCapacityValidate: fetchCapacityValidate,
    liveFetchFailModeOpen: liveFetchFailModeOpen,
    resolveValidateApiUrl: resolveValidateApiUrl,
    resolveEventsApiUrl: resolveEventsApiUrl,
    buildRequest: buildRequest,
    fetchNearbyCatalog: fetchNearbyCatalog,
    fetchStateCatalog: fetchStateCatalog,
    resolveHubSummaryUrl: resolveHubSummaryUrl,
    fetchHubSummary: fetchHubSummary,
    fetchLiveState: fetchLiveState,
    deriveLiveStateApiUrl: deriveLiveStateApiUrl,
    resolveLiveStateApiUrl: resolveLiveStateApiUrl,
    resolveLiveStreamUrl: resolveLiveStreamUrl,
    isSlotBookable: isSlotBookable,
    remainingSeats: remainingSeats,
    eventIdAliases: eventIdAliases,
    eventGidFromCartLine: eventGidFromCartLine,
    seatsInCartForEvent: seatsInCartForEvent,
    seatsInCartForSlot: seatsInCartForSlot,
    fetchCart: fetchCart,
    fetchCartSeatsForEvent: fetchCartSeatsForEvent,
    fetchCartSeatsForSlot: fetchCartSeatsForSlot,
    mergeSlotWithLive: mergeSlotWithLive,
    mergeSlotsWithLive: mergeSlotsWithLive,
    collectEventGids: collectEventGids,
    startLiveStatePoll: startLiveStatePoll,
    extractSlotsPayload: extractSlotsPayload,
    extractConfigVersion: extractConfigVersion,
    normalizeSlot: normalizeSlot,
    normalizeCity: normalizeCity,
  };
})(typeof window !== 'undefined' ? window : this);
